/* Types */
import {
  MenuPageBuilder,
  menuPageBuilderFooterSpacingDefaults,
  sanitizeMenuPageBuilder,
} from 'src/shared/dbTypes/menuPageBuilder'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'
import { useRouter } from 'next/router'
import config from 'src/config'
import { usesCompactCheckoutFooter } from 'src/functions/checkoutVariant'
import { getNewsletterStorefront } from 'src/functions/newsletterStorefront'

/* Components */
import TopMarken from './TopMarken'
import InfosUndLinks from './InfosUndLinks'
import Copyright from './Copyright'
import Timestamp from './Timestamp'
import Fussnoten from './Fussnoten'
import CheckoutFooter from './CheckoutFooter'
import FooterPresentation from './FooterPresentation'

interface Props {
  pageBuilder?: MenuPageBuilder
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const Footer: React.FC<Props> = ({ pageBuilder, landingPageImageAiGenerated }) => {
  const { pathname } = useRouter()
  const safeBuilder = sanitizeMenuPageBuilder(pageBuilder)
  const footer = safeBuilder?.footer
  const footerEnabled = Boolean(footer?.enabled)
  const editorialFooter = Boolean(getNewsletterStorefront(config.shop?.shop))
  const showTimestamp = process.env.NEXT_PUBLIC_TIMESTAMP && (
    footerEnabled
      ? footer?.showTimestamp !== false
      : true
  )

  if (!config.footerMobileAccordions && usesCompactCheckoutFooter(config.compactCheckoutFooter, pathname)) {
    return <CheckoutFooter footer={footer} />
  }

  if (!footerEnabled || !footer) {
    return <FooterPresentation collapsible={config.footerMobileAccordions} pathname={pathname} showContact={editorialFooter}
      copyright={<Copyright includeLegalLinks={config.footerMobileAccordions} />} footnotes={<Fussnoten />}>
      {!editorialFooter && <TopMarken landingPageImageAiGenerated={landingPageImageAiGenerated} />}
      <InfosUndLinks accessibleLinks={config.footerMobileAccordions} landingPageImageAiGenerated={landingPageImageAiGenerated} />
      {showTimestamp && <Timestamp />}
    </FooterPresentation>
  }

  const dividerMarginTop = footer.dividerMarginTop ?? menuPageBuilderFooterSpacingDefaults.dividerMarginTop
  const effectiveDividerMarginTop = footer.showDivider
    ? Math.max(32, dividerMarginTop)
    : dividerMarginTop
  const dividerStyle = {
    backgroundColor: footer.surfaceColor || footer.backgroundColor,
    borderBottom: `1px solid ${footer.mutedTextColor || 'rgba(42, 33, 26, 0.16)'}`,
    height: `${effectiveDividerMarginTop}px`,
  }
  const infosPaddingTop = footer.infosPaddingTop ?? menuPageBuilderFooterSpacingDefaults.infosPaddingTop
  const adjustedInfosPaddingTop = footer.showDivider
    ? Math.max(0, infosPaddingTop - effectiveDividerMarginTop)
    : infosPaddingTop
  const infosPaddingBottom = footer.infosPaddingBottom ?? menuPageBuilderFooterSpacingDefaults.infosPaddingBottom
  const legalPaddingTop = footer.legalPaddingTop ?? menuPageBuilderFooterSpacingDefaults.legalPaddingTop
  const legalPaddingBottom = footer.legalPaddingBottom ?? menuPageBuilderFooterSpacingDefaults.legalPaddingBottom
  const footnotesPaddingTop = footer.footnotesPaddingTop ?? menuPageBuilderFooterSpacingDefaults.footnotesPaddingTop
  const footnotesPaddingBottom = footer.footnotesPaddingBottom ?? menuPageBuilderFooterSpacingDefaults.footnotesPaddingBottom

  return <FooterPresentation collapsible={config.footerMobileAccordions} pathname={pathname} showContact={editorialFooter}
    backgroundColor={config.footerMobileAccordions ? footer.legalBackgroundColor || footer.surfaceColor || footer.backgroundColor : footer.surfaceColor || footer.backgroundColor}
    textColor={footer.mutedTextColor || footer.textColor}
    copyright={<Copyright
      text={footer.copyrightText}
      includeLegalLinks={config.footerMobileAccordions}
      backgroundColor={footer.legalBackgroundColor || footer.surfaceColor}
      textColor={footer.mutedTextColor || footer.textColor}
      paddingTop={legalPaddingTop}
      paddingBottom={legalPaddingBottom}
    />}
    footnotes={footer.showFootnotes !== false &&
      <Fussnoten
        backgroundColor={footer.backgroundColor}
        textColor={footer.mutedTextColor || footer.textColor}
        linkColor={footer.accentColor}
        primaryHtml={footer.footnotesPrimaryHtml}
        secondaryHtml={footer.footnotesSecondaryHtml}
        paddingTop={footnotesPaddingTop}
        paddingBottom={footnotesPaddingBottom}
      />
    }>
    {footer.showDivider &&
      <div style={dividerStyle} />
    }

    {!editorialFooter && footer.showTopBrands !== false &&
      <TopMarken
        backgroundColor={footer.brandSectionBackgroundColor || footer.accentColor || footer.backgroundColor}
        textColor={footer.textColor}
        brandBlock={footer.topBrandsBlock}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    }

    {footer.showInfosLinks !== false &&
      <InfosUndLinks
        backgroundColor={footer.surfaceColor || footer.backgroundColor}
        textColor={footer.textColor}
        columns={footer.columns}
        columnsPerRow={footer.columnsPerRow}
        accessibleLinks={config.footerMobileAccordions}
        paddingTop={adjustedInfosPaddingTop}
        paddingBottom={infosPaddingBottom}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    }

    {showTimestamp &&
      <Timestamp
        backgroundColor={footer.legalBackgroundColor || footer.surfaceColor}
        textColor={footer.mutedTextColor || footer.textColor}
        paddingTop={legalPaddingTop}
        paddingBottom={legalPaddingBottom}
      />
    }

  </FooterPresentation>
}

export default Footer
